import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/layoutUser';

const Leaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('all');
  const [userProfile, setUserProfile] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [animate, setAnimate] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showUserCard, setShowUserCard] = useState(false);
  const itemsPerPage = 99;

  // Level configuration with new colors
  const LEVELS = {
    1: { name: 'Pemula Buku', color: '#3C8DBC' },
    2: { name: 'Penjelajah Kata', color: '#3C8DBC' },
    3: { name: 'Penakluk Halaman', color: '#3C8DBC' },
    4: { name: 'Sang Cendekia', color: '#3C8DBC' },
    5: { name: 'Penjaga Pengetahuan', color: '#3C8DBC' }
  };

  const API_URL = process.env.REACT_APP_API_URL || `${process.env.REACT_APP_API_URL}`;

  const getToken = () => localStorage.getItem('token');
  const getCurrentUser = () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);
    setAnimate(false);
    
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/gamification/leaderboard`, {
        params: { limit: 99, period: period },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setLeaderboardData(response.data.leaderboard);
        setTimeout(() => setAnimate(true), 100);
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Gagal memuat data leaderboard. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.id) return;

      const token = getToken();
      const response = await axios.get(`${API_URL}/gamification/profile/${currentUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUserProfile(response.data.user);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
    fetchUserProfile();
  }, [period]);

  const getProfilePhoto = (photo) => {
    if (!photo) return '/images/default-avatar.png';
    if (photo.startsWith('http')) return photo;
    return `${API_URL.replace('/api', '')}${photo}`;
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = leaderboardData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(leaderboardData.length / itemsPerPage);

  // Render Podium Top 3
  const renderPodium = () => {
    if (leaderboardData.length < 3) return null;

    const top3 = [
      leaderboardData[1], // Rank 2 (left)
      leaderboardData[0], // Rank 1 (center)
      leaderboardData[2]  // Rank 3 (right)
    ];

    const crownStyles = [
      { color: '#c0c0c0', size: isMobile ? '2rem' : '2.5rem' },
      { color: '#3C8DBC', size: isMobile ? '2.5rem' : '3rem' },
      { color: '#cd7f32', size: isMobile ? '2rem' : '2.5rem' }
    ];

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        gap: isMobile ? '0.5rem' : '1rem',
        marginBottom: '2rem',
        padding: isMobile ? '1rem 0.5rem' : '2rem 1rem',
        opacity: animate ? 1 : 0,
        transform: animate ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.6s ease-out'
      }}>
        {top3.map((user, index) => {
          if (!user) return null;
          const actualRank = index === 0 ? 2 : index === 1 ? 1 : 3;
          const isFirst = actualRank === 1;
          
          return (
            <div 
              key={user.id_anggota}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                maxWidth: isMobile ? '100px' : '150px',
                marginTop: isFirst ? '0' : isMobile ? '1rem' : '2rem',
                opacity: animate ? 1 : 0,
                transform: animate ? 'scale(1)' : 'scale(0.8)',
                transition: `all 0.5s ease-out ${index * 0.1}s`
              }}
            >
              {/* Crown */}
              <div style={{ 
                fontSize: crownStyles[index].size,
                marginBottom: '0.5rem',
                filter: `drop-shadow(0 0 10px ${crownStyles[index].color})`,
                animation: animate ? 'bounce 1s ease-in-out infinite' : 'none'
              }}>
                👑
              </div>

              {/* Profile Photo */}
              <div style={{
                width: isMobile ? (isFirst ? '70px' : '60px') : (isFirst ? '100px' : '80px'),
                height: isMobile ? (isFirst ? '70px' : '60px') : (isFirst ? '100px' : '80px'),
                borderRadius: '50%',
                border: `${isMobile ? '3px' : '4px'} solid ${crownStyles[index].color}`,
                overflow: 'hidden',
                marginBottom: '0.5rem',
                boxShadow: `0 8px 20px ${crownStyles[index].color}60`
              }}>
                <img 
                  src={getProfilePhoto(user.foto_profile)} 
                  alt={user.nama_lengkap}
                  style={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>

              {/* Name */}
              <p style={{
                color: '#343A40',
                fontWeight: 'bold',
                fontSize: isMobile ? '0.75rem' : (isFirst ? '1rem' : '0.875rem'),
                textAlign: 'center',
                marginBottom: '0.25rem',
                wordBreak: 'break-word',
                lineHeight: 1.2
              }}>
                {user.nama_lengkap}
              </p>

              {/* Rank Badge */}
              <div style={{
                background: crownStyles[index].color,
                color: '#FFFFFF',
                padding: isMobile ? '0.3rem 0.6rem' : '0.4rem 0.8rem',
                borderRadius: '50px',
                fontSize: isMobile ? '0.7rem' : '0.8rem',
                fontWeight: 'bold',
                marginBottom: '0.25rem',
                boxShadow: `0 4px 12px ${crownStyles[index].color}80`
              }}>
                Peringkat {actualRank}
              </div>

              {/* Stats */}
              <p style={{
                color: '#3C8DBC',
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                fontWeight: 'bold',
                textAlign: 'center'
              }}>
                {user.total_exp} EXP
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  // Render Table
  const renderTable = () => {
    return (
      <div style={{ 
        background: 'transparent',
        borderRadius: '20px',
        padding: '0',
        overflowX: 'auto',
        opacity: animate ? 1 : 0,
        transform: animate ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.6s ease-out 0.2s'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: '0 0.5rem',
          fontSize: isMobile ? '0.75rem' : '0.875rem',
          color: '#343A40'
        }}>
          <thead>
            <tr style={{ 
              color: '#343A40'
            }}>
              <th style={{ 
                color: '#343A40', 
                textAlign: 'center', 
                padding: isMobile ? '0.75rem 0.25rem' : '1rem',
                width: isMobile ? '40px' : '60px',
                fontWeight: 'bold'
              }}>#</th>
              <th style={{ 
                color: '#343A40',
                textAlign: 'left',
                padding: isMobile ? '0.75rem 0.25rem' : '1rem',
                fontWeight: 'bold'
              }}>Nama</th>
              <th style={{ 
                color: '#343A40', 
                textAlign: 'center',
                padding: isMobile ? '0.75rem 0.25rem' : '1rem',
                width: isMobile ? '50px' : '80px',
                fontWeight: 'bold'
              }}>Level</th>
              <th style={{ 
                color: '#343A40', 
                textAlign: 'center',
                padding: isMobile ? '0.75rem 0.25rem' : '1rem',
                width: isMobile ? '60px' : '90px',
                fontWeight: 'bold'
              }}>XP</th>
              {!isMobile && (
                <>
                  <th style={{ color: '#343A40', textAlign: 'center', padding: '1rem', fontWeight: 'bold' }}>Peminjaman</th>
                  <th style={{ color: '#343A40', textAlign: 'center', padding: '1rem', fontWeight: 'bold' }}>Tepat Waktu</th>
                  <th style={{ color: '#343A40', textAlign: 'center', padding: '1rem', fontWeight: 'bold' }}>Progress</th>
                  <th style={{ color: '#343A40', textAlign: 'center', padding: '1rem', fontWeight: 'bold' }}>Sisa XP</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {currentItems.map((user, index) => {
              const isCurrentUser = userProfile && user.id_anggota === userProfile.id_anggota;
              const levelColor = LEVELS[user.level]?.color || '#3C8DBC';
              
              return (
                <tr 
                  key={user.id_anggota}
                  style={{
                    background: isCurrentUser 
                      ? 'rgba(60, 141, 188, 0.2)'
                      : '#FFFFFF',
                    transition: 'all 0.3s ease',
                    opacity: animate ? 1 : 0,
                    transform: animate ? 'translateX(0)' : 'translateX(-20px)',
                    transitionDelay: `${index * 0.02}s`,
                    boxShadow: isCurrentUser 
                      ? '0 4px 16px rgba(60, 141, 188, 0.3)'
                      : '0 2px 8px rgba(0,0,0,0.1)',
                    borderRadius: '12px'
                  }}
                >
                  <td style={{ 
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '1.1rem' : '1.5rem',
                    color: user.rank <= 3 ? '#3C8DBC' : '#343A40',
                    padding: isMobile ? '0.75rem 0.25rem' : '1rem',
                    textShadow: user.rank <= 3 ? '0 2px 8px rgba(60, 141, 188, 0.3)' : 'none',
                    borderTopLeftRadius: '12px',
                    borderBottomLeftRadius: '12px'
                  }}>
                    {user.rank}
                  </td>

                  <td style={{ padding: isMobile ? '0.75rem 0.25rem' : '1rem' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: isMobile ? '0.5rem' : '0.75rem'
                    }}>
                      <div style={{
                        width: isMobile ? '36px' : '44px',
                        height: isMobile ? '36px' : '44px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: `3px solid ${levelColor}`,
                        flexShrink: 0,
                        boxShadow: `0 4px 12px ${levelColor}40`
                      }}>
                        <img 
                          src={getProfilePhoto(user.foto_profile)} 
                          alt={user.nama_lengkap}
                          style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ 
                          color: '#343A40',
                          fontWeight: 'bold',
                          marginBottom: isMobile ? '0' : '0.125rem',
                          fontSize: isMobile ? '0.8rem' : '0.95rem',
                          whiteSpace: isMobile ? 'nowrap' : 'normal',
                          overflow: isMobile ? 'hidden' : 'visible',
                          textOverflow: isMobile ? 'ellipsis' : 'clip'
                        }}>
                          {user.nama_lengkap}
                        </p>
                        {!isMobile && (
                          <p style={{ 
                            color: '#6c757d',
                            fontSize: '0.8rem'
                          }}>
                            {user.kelas}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  <td style={{ 
                    textAlign: 'center',
                    padding: isMobile ? '0.75rem 0.25rem' : '1rem'
                  }}>
                    <span style={{
                      background: levelColor,
                      color: '#FFFFFF',
                      padding: isMobile ? '0.3rem 0.5rem' : '0.4rem 0.9rem',
                      borderRadius: '50px',
                      fontSize: isMobile ? '0.75rem' : '0.9rem',
                      fontWeight: 'bold',
                      display: 'inline-block',
                      boxShadow: `0 4px 12px ${levelColor}60`
                    }}>
                      {user.level}
                    </span>
                  </td>

                  <td style={{ 
                    textAlign: 'center',
                    color: '#3C8DBC',
                    fontWeight: 'bold',
                    padding: isMobile ? '0.75rem 0.25rem' : '1rem',
                    fontSize: isMobile ? '0.85rem' : '1rem',
                    textShadow: '0 2px 8px rgba(60, 141, 188, 0.2)',
                    borderTopRightRadius: isMobile ? '12px' : '0',
                    borderBottomRightRadius: isMobile ? '12px' : '0'
                  }}>
                    {user.total_exp}
                  </td>

                  {!isMobile && (
                    <>
                      <td style={{ 
                        textAlign: 'center',
                        color: '#3C8DBC',
                        fontWeight: 'bold',
                        padding: '1rem'
                      }}>
                        {user.total_peminjaman}
                      </td>

                      <td style={{ 
                        textAlign: 'center',
                        color: '#3C8DBC',
                        fontWeight: 'bold',
                        padding: '1rem'
                      }}>
                        {user.pengembalian_tepat_waktu}
                      </td>

                      <td style={{ 
                        textAlign: 'center',
                        color: '#3C8DBC',
                        fontWeight: 'bold',
                        padding: '1rem'
                      }}>
                        {Math.round(user.progress_percentage)}%
                      </td>

                      <td style={{ 
                        textAlign: 'center',
                        color: '#3C8DBC',
                        fontWeight: 'bold',
                        padding: '1rem',
                        borderTopRightRadius: '12px',
                        borderBottomRightRadius: '12px'
                      }}>
                        {user.level < 5 ? user.exp_to_next_level : 'MAX'}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: 'none',
                background: currentPage === 1 ? 'rgba(108, 117, 125, 0.2)' : '#3C8DBC',
                color: currentPage === 1 ? '#6c757d' : '#FFFFFF',
                fontWeight: 'bold',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              ← Sebelumnya
            </button>
            
            <span style={{ color: '#343A40', fontWeight: 'bold' }}>
              Halaman {currentPage} dari {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: 'none',
                background: currentPage === totalPages ? 'rgba(108, 117, 125, 0.2)' : '#3C8DBC',
                color: currentPage === totalPages ? '#6c757d' : '#FFFFFF',
                fontWeight: 'bold',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Selanjutnya →
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render User EXP Card as Modal
  const renderUserExpModal = () => {
    if (!userProfile || !showUserCard) return null;

    const levelInfo = LEVELS[userProfile.level] || LEVELS[1];
    const isInLeaderboard = leaderboardData.some(u => u.id_anggota === userProfile.id_anggota);

    return (
      <>
        {/* Modal Overlay */}
        <div 
          onClick={() => setShowUserCard(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideUp {
                from { transform: translateY(50px); opacity: 0; }
                to { transform: translateY(0); opacity: 1; }
              }
            `}
          </style>

          {/* Modal Content */}
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: isMobile ? '1.5rem' : '2rem',
              maxWidth: '600px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(60, 141, 188, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowUserCard(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'rgba(52, 58, 64, 0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '1.5rem',
                color: '#343A40',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(52, 58, 64, 0.2)';
                e.target.style.transform = 'rotate(90deg)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(52, 58, 64, 0.1)';
                e.target.style.transform = 'rotate(0deg)';
              }}
            >
              ×
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <span style={{ fontSize: '2rem' }}>📊</span>
              <h3 style={{ 
                color: '#343A40',
                fontSize: isMobile ? '1.5rem' : '1.75rem',
                fontWeight: 'bold',
                margin: 0
              }}>
                Profil EXP Anda
              </h3>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              gap: '1.5rem'
            }}>
              {/* Avatar */}
              <div style={{
                width: isMobile ? '100px' : '120px',
                height: isMobile ? '100px' : '120px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '5px solid #3C8DBC',
                flexShrink: 0,
                boxShadow: '0 10px 30px rgba(60, 141, 188, 0.3)'
              }}>
                <img 
                  src={getProfilePhoto(userProfile.foto_profile)} 
                  alt={userProfile.nama_lengkap}
                  style={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>

              {/* User Info */}
              <div style={{ flex: 1, width: '100%' }}>
                <p style={{ 
                  color: '#343A40',
                  fontSize: isMobile ? '1.3rem' : '1.5rem',
                  fontWeight: 'bold',
                  marginBottom: '0.75rem'
                }}>
                  {userProfile.nama_lengkap}
                </p>
                
                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  marginBottom: '1.25rem',
                  flexWrap: 'wrap'
                }}>
                  <div style={{
                    background: '#3C8DBC',
                    padding: '0.5rem 1rem',
                    borderRadius: '50px',
                    fontSize: '0.95rem',
                    fontWeight: 'bold',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 12px rgba(60, 141, 188, 0.3)'
                  }}>
                    🏆 Peringkat: #{userProfile.rank}
                  </div>
                  <div style={{
                    background: levelInfo.color,
                    padding: '0.5rem 1rem',
                    borderRadius: '50px',
                    fontSize: '0.95rem',
                    fontWeight: 'bold',
                    color: '#FFFFFF',
                    boxShadow: `0 4px 12px ${levelInfo.color}60`
                  }}>
                    ⭐ Level {userProfile.level}
                  </div>
                </div>

                {/* Stats Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '0.75rem',
                  marginBottom: '1.25rem'
                }}>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '0.75rem',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '2px solid #e9ecef'
                  }}>
                    <p style={{ color: '#6c757d', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                      Total EXP
                    </p>
                    <p style={{ color: '#3C8DBC', fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                      {userProfile.total_exp}
                    </p>
                  </div>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '0.75rem',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '2px solid #e9ecef'
                  }}>
                    <p style={{ color: '#6c757d', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                      Peminjaman
                    </p>
                    <p style={{ color: '#3C8DBC', fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                      {userProfile.total_peminjaman}
                    </p>
                  </div>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '0.75rem',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '2px solid #e9ecef'
                  }}>
                    <p style={{ color: '#6c757d', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                      Tepat Waktu
                    </p>
                    <p style={{ color: '#3C8DBC', fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                      {userProfile.pengembalian_tepat_waktu}
                    </p>
                  </div>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '0.75rem',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '2px solid #e9ecef'
                  }}>
                    <p style={{ color: '#6c757d', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                      Progress
                    </p>
                    <p style={{ color: '#3C8DBC', fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                      {Math.round(userProfile.progress_percentage)}%
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <p style={{ 
                    color: '#343A40',
                    fontSize: '0.9rem',
                    marginBottom: '0.5rem',
                    fontWeight: 'bold'
                  }}>
                    Progress ke {levelInfo.name}
                  </p>
                  <div style={{
                    background: '#e9ecef',
                    borderRadius: '50px',
                    height: '28px',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{
                      background: '#3C8DBC',
                      height: '100%',
                      width: `${userProfile.progress_percentage}%`,
                      borderRadius: '50px',
                      transition: 'width 0.5s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(60, 141, 188, 0.4)'
                    }}>
                      <span style={{
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        color: '#FFFFFF'
                      }}>
                        {Math.round(userProfile.progress_percentage)}%
                      </span>
                    </div>
                  </div>
                </div>

                <p style={{ 
                  color: '#343A40',
                  fontSize: '0.95rem',
                  textAlign: 'center',
                  padding: '0.75rem',
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  border: '2px solid #e9ecef'
                }}>
                  {userProfile.level < 5 ? (
                    <>
                      🎯 <strong>{userProfile.exp_to_next_level} EXP</strong> lagi ke level berikutnya
                    </>
                  ) : (
                    <>
                      👑 <strong>LEVEL MAKSIMAL</strong> - Anda adalah Penjaga Pengetahuan!
                    </>
                  )}
                </p>

                {!isInLeaderboard && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: 'rgba(60, 141, 188, 0.1)',
                    borderRadius: '12px',
                    border: '2px solid rgba(60, 141, 188, 0.3)'
                  }}>
                    <p style={{
                      color: '#3C8DBC',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      margin: 0
                    }}>
                      💪 Terus tingkatkan EXP untuk masuk leaderboard!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <Layout>
      <style>
        {`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        `}
      </style>
      <div style={{ 
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '1rem' : '1.5rem',
        paddingTop: isMobile ? '0.5rem' : '0.5rem'
      }}>
        {/* Loading State */}
        {loading && (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid rgba(60, 141, 188, 0.2)',
              borderTop: '4px solid #3C8DBC',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
            <style>
              {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
            </style>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{
            background: 'rgba(220, 53, 69, 0.1)',
            border: '2px solid #dc3545',
            borderRadius: '12px',
            padding: '1rem',
            color: '#dc3545',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Podium Top 3 - PALING ATAS */}
        {!loading && !error && leaderboardData.length >= 3 && renderPodium()}

        {/* Period Filter Button - SETELAH PODIUM */}
        {!loading && !error && (
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '2rem'
          }}>
            <div style={{ 
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: 'center',
              opacity: animate ? 1 : 0,
              transition: 'all 0.5s ease-out 0.1s'
            }}>
              {[
                { value: 'all', label: 'Semua Waktu' },
                { value: 'monthly', label: 'Bulan Ini' },
                { value: 'weekly', label: 'Minggu Ini' }
              ].map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  style={{ 
                    padding: isMobile ? '0.5rem 1rem' : '0.6rem 1.2rem',
                    borderRadius: '50px',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    fontWeight: 'bold',
                    border: 'none',
                    cursor: 'pointer',
                    background: period === p.value 
                      ? '#3C8DBC' 
                      : 'rgba(52, 58, 64, 0.1)',
                    color: period === p.value ? '#FFFFFF' : '#343A40',
                    transition: 'all 0.3s ease',
                    boxShadow: period === p.value ? '0 4px 12px rgba(60, 141, 188, 0.3)' : 'none'
                  }}
                >
                  {p.label}
                </button>
              ))}
              
              {/* EXP Saya Button */}
              {userProfile && (
                <button
                  onClick={() => setShowUserCard(!showUserCard)}
                  style={{ 
                    padding: isMobile ? '0.5rem 1rem' : '0.6rem 1.2rem',
                    borderRadius: '50px',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    fontWeight: 'bold',
                    border: 'none',
                    cursor: 'pointer',
                    background: showUserCard 
                      ? '#343A40' 
                      : 'rgba(60, 141, 188, 0.2)',
                    color: '#FFFFFF',
                    transition: 'all 0.3s ease',
                    boxShadow: showUserCard ? '0 4px 12px rgba(52, 58, 64, 0.3)' : 'none'
                  }}
                >
                  📊 EXP Saya
                </button>
              )}
            </div>
          </div>
        )}

        {/* User EXP Card - Toggle with button */}
        {!loading && !error && renderUserExpModal()}

        {/* Leaderboard Table - PALING BAWAH */}
        {!loading && !error && leaderboardData.length > 0 && renderTable()}
      </div>
    </Layout>
  );
};

export default Leaderboard;